import { useEffect, useState, useRef, KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Button,
    Form,
    Modal,
    InputGroup,
    FormControl,
    Breadcrumb,
} from "react-bootstrap";
import { ReactComponent as EditIcon } from "assets/svg/edit.svg";
import { useAppContext } from "context/appContext";
import {
    MinNameChar,
    MaxNameChar,
    PHONE_NUMBER_VALIDATION_MESSAGE,
    Patterns,
    SecondsToCountdown,
    ACTION_FAIL_MESSAGE,
    RESEND_VERIFY_CODE_MESSAGE,
    SIGN_UP_VERIFY_CODE_MESSAGE,
} from "constant";
import AccountService from "service/accountService";
import { storeUserAction } from "store/actions";
import { isHttpSuccess, renderCountdown } from "utils/functions";
import DefaultModal, { LoadingModal } from "components/modals/ModalTemplate";
import "./UserProfile.css";
import { get, pick as _pick, isEqual as _isEqual } from "lodash";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { styled } from "@mui/system";
import ContentWrapper, {
    SimpleModalDataType,
} from "components/content-wrapper/ContentWrapper";
import { UserDto } from "types";
import { getAPIError, showErrorAlert, showSuccessAlert } from "utils/alert";
import { APP_CONFIG } from "config/appConfig";

const StyledPhoneInput = styled(PhoneInput)`
    input {
        border-color: transparent;
    }
`;

const CustomSwitch = styled(Form.Switch)`
    display: inline-block;
    padding-left: 0;

    .custom-control-input {
        right: 0;
        left: auto;
    }

    .custom-control-label {
        &::before {
            right: -2.5rem;
            left: auto;
        }

        &::after {
            right: -0.9rem;
            left: auto;
        }
    }
`;

const getUserData = (data: any) => ({
    givenName: get(data, "givenName", "").trim(),
    familyName: get(data, "familyName", "").trim(),
    email: get(data, "email", "").trim(),
    phoneNumber: get(data, "phoneNumber", "").trim(),
    emailVerified: get(data, "emailVerified", false),
    phoneNumberVerified: get(data, "phoneNumberVerified", false),
    timezone: get(data, "timezone", "").trim(),
});

const UserProfile = () => {
    const { storeData, storeDispatchActions } = useAppContext();
    const { user } = storeData;
    const { isLoaded: isUserLoaded, userData } = user;
    const mfaRef: any = useRef(null);
    const [isEdit, setIsEdit] = useState(false);
    const [localUserData, setLocalUserData] = useState<UserDto>(
        getUserData(userData)
    );
    const [isMFAEnable, setIsMFAEnable] = useState(false);
    const [verifyCode, setVerifyCode] = useState("");
    const [codeError, setCodeError] = useState("");
    const [isShow, setIsShow] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [modalShow, setModalShow] = useState(false);
    const [modalType, setModalType] = useState("");
    const [modalContent, setModalContent] = useState("");
    const [isModalLoaded, setIsModalLoaded] = useState(true);
    const [simpleModalData, setSimpleModalData] =
        useState<null | SimpleModalDataType>(null);

    const phoneNumRef: any = useRef();
    const changeWithDebounce = (e: any) => {
        const { value, name } = e.target;

        if (value.startsWith(" ")) return;

        setLocalUserData((current) => ({
            ...current,
            [name]: value,
        }));
    };

    useEffect(() => {
        setLocalUserData(getUserData(userData));
        setIsMFAEnable(
            userData.userMfaSettingList?.includes("SMS_MFA") || false
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserLoaded, userData]);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setInterval(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => {
                clearInterval(timer);
            };
        }
    }, [countdown, setCountdown]);

    const handleClickEdit = async () => {
        setIsEdit(true);
    };

    const handleMFAChange = async (e: any) => {
        const { checked } = e.target;

        if (!localUserData.phoneNumber) {
            mfaRef.current.checked = false;
            return setSimpleModalData({
                type: "error",
                body: "Phone number is required",
            } as SimpleModalDataType);
        }

        const mfaRes = await AccountService.updateMFAStatus({
            name: "SMS",
            enable: checked,
        });

        if (isHttpSuccess(mfaRes.status)) {
            const mfaSettings = userData.userMfaSettingList || [];
            if (!userData.userMfaSettingList?.includes("SMS_MFA")) {
                mfaSettings.push("SMS_MFA");
            }

            setIsMFAEnable(checked);
            storeDispatchActions(
                storeUserAction({
                    userData: {
                        ...userData,
                        userMfaSettingList: mfaSettings,
                    },
                })
            );
            showSuccessAlert({
                message: `MFA has been ${checked ? "enabled" : "disabled"
                    } successfully.`,
            });
        } else {
            setIsMFAEnable(!checked);
            showErrorAlert(getAPIError(mfaRes, ACTION_FAIL_MESSAGE));
        }
    };

    const validateFormData = () => {
        const minNameLength = 1;
        const maxNameLength = 255;

        const checkNameLength = (name: string) =>
            name.length >= MinNameChar && name.length <= MaxNameChar;

        let isValid = true;
        let errorMess = "";
        const { givenName, familyName, phoneNumber } = localUserData;

        if (!checkNameLength(givenName)) {
            isValid = false;
            errorMess = `First name must be between ${minNameLength} and ${maxNameLength} characters`;
        } else if (!checkNameLength(familyName)) {
            isValid = false;
            errorMess = `Last name must be between ${minNameLength} and ${maxNameLength} characters`;
        } else if (
            phoneNumber &&
            (!Patterns.phoneNumberPattern.test(phoneNumber) ||
                phoneNumRef.current.classList.contains("is-invalid"))
        ) {
            isValid = false;
            errorMess = "Invalid mobile number";
        }
        return { isValid, errorMess };
    };

    const handleSave = async () => {
        const { phoneNumber, givenName, familyName } = _pick(
            getUserData(localUserData),
            ["givenName", "familyName", "phoneNumber"]
        );
        const originalUserData = _pick(getUserData(userData), [
            "givenName",
            "familyName",
            "phoneNumber",
        ]);

        if (
            _isEqual({ phoneNumber, givenName, familyName }, originalUserData)
        ) {
            setSimpleModalData({
                type: "error",
                body: `Nothing changed`,
            } as SimpleModalDataType);
            return;
        }
        const { isValid, errorMess }: any = validateFormData();
        if (!isValid) {
            setSimpleModalData({
                type: "error",
                body: errorMess,
            } as SimpleModalDataType);
            setIsEdit(true);
            return;
        }

        const payload: {
            first_name: string;
            last_name: string;
            phone?: string;
        } = {
            first_name: givenName,
            last_name: familyName,
        };
        if (!phoneNumber || phoneNumber !== userData.phoneNumber) {
            payload.phone = phoneNumber || "";
        }

        // Onprem does not support phone number
        if (APP_CONFIG.APP_IS_ONPREM) {
            delete payload.phone;
        }

        const updateRes = await AccountService.updateAccountInfo(payload);

        if (isHttpSuccess(updateRes.status)) {
            // Remove verified icon if number is being updated or removed
            if (phoneNumber !== userData.phoneNumber) {
                setLocalUserData({
                    ...localUserData,
                    phoneNumber,
                    phoneNumberVerified: false,
                });
                storeDispatchActions(
                    storeUserAction({
                        userData: {
                            ...userData,
                            phone_number: phoneNumber,
                            phone_number_verified: false,
                        },
                    })
                );
            }
            const userInfoRes: any = await AccountService.getAccountInfo();
            if (userInfoRes.status === 200) {
                setIsEdit(false);
                storeDispatchActions(
                    storeUserAction({
                        userData: userInfoRes.data,
                        isLoaded: true,
                    })
                );

                setSimpleModalData({
                    resObj: userInfoRes,
                    body: `User profile information has been updated.`,
                } as SimpleModalDataType);
            }
            return;
        }

        const message = get(
            updateRes,
            "data.description",
            "Something went wrong."
        );

        setSimpleModalData({
            type: "error",
            title: "Error",
            body: message,
        } as SimpleModalDataType);
    };
    const handleCancel = async () => {
        setLocalUserData(userData);
        setIsEdit(false);
    };

    const handleCloseModal = () => {
        setCountdown(0);
        setIsShow(false);
        setVerifyCode("");
    };

    const handleSubmitCode = async () => {
        const _code = verifyCode.trim();
        if (!Patterns.codePattern.test(_code)) {
            setSimpleModalData({
                body: SIGN_UP_VERIFY_CODE_MESSAGE,
            } as SimpleModalDataType);
            return;
        }

        const res: any = await AccountService.submitVerifyCode({
            code: _code,
        });

        if (isHttpSuccess(res.status)) {
            setIsShow(false);
            setVerifyCode("");

            setSimpleModalData({
                type: "success",
                body: `Phone number has been verified.`,
            } as SimpleModalDataType);

            setLocalUserData({
                ...localUserData,
                phoneNumberVerified: true,
            });
            storeDispatchActions(
                storeUserAction({
                    userData: {
                        ...userData,
                        phone_number_verified: true,
                    },
                })
            );
        } else {
            setSimpleModalData({
                resObj: res,
                body: get(res, "data.description", SIGN_UP_VERIFY_CODE_MESSAGE),
            } as SimpleModalDataType);
            setCodeError("Invalid verification code.");
        }
    };

    const handleClickVerify = async () => {
        setIsModalLoaded(false);
        setCodeError("");
        const codeRes: any = await AccountService.getVerifyCode();

        setIsModalLoaded(true);
        if (isHttpSuccess(codeRes?.status)) {
            showSuccessAlert({
                message: "A new Verification Code has been sent!",
            });
            setIsShow(true);
            setCountdown(SecondsToCountdown);
        } else {
            showErrorAlert({
                message: codeRes?.data?.description || "Something went wrong.",
            });
        }
    };

    const handleChangeVerifyCode = (e: any) => {
        setVerifyCode(e.target.value);
    };

    const openConfirmationResendModal = () => {
        setModalShow(true);
        setModalType("resendConfirm");
        setModalContent(RESEND_VERIFY_CODE_MESSAGE);
    };

    const submitForm = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSave();
        }
    };

    return (
        <ContentWrapper simpleModalData={simpleModalData}>
            <div className="page-content user-profile">
                <Container>
                    <Row>
                        <Col sm={12}>
                            <h5 className="page-title">Profile</h5>
                            {!isEdit && (
                                <Button
                                    variant="primary"
                                    className="pr-4 float-right"
                                    onClick={handleClickEdit}
                                >
                                    <span>
                                        <EditIcon className="mr-2 ml-1" />
                                        Edit
                                    </span>
                                </Button>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <Breadcrumb className="w-100">
                                <Breadcrumb.Item
                                    linkAs={Link}
                                    linkProps={{ to: "/user/settings" }}
                                >
                                    Account Settings
                                </Breadcrumb.Item>
                                <Breadcrumb.Item active>
                                    Profile
                                </Breadcrumb.Item>
                            </Breadcrumb>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm="12">
                            <Form>
                                <div className="form-box">
                                    <Row>
                                        <Form.Group as={Col} className="mb-4">
                                            <Form.Label>First Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                required
                                                aria-label="first_name"
                                                name="givenName"
                                                value={localUserData.givenName}
                                                onChange={changeWithDebounce}
                                                disabled={!isEdit}
                                                onKeyPress={submitForm}
                                            />
                                        </Form.Group>
                                        <Form.Group as={Col} className="mb-4">
                                            <Form.Label>Last Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                required
                                                aria-label="last_name"
                                                name="familyName"
                                                value={localUserData.familyName}
                                                onChange={changeWithDebounce}
                                                disabled={!isEdit}
                                                onKeyPress={submitForm}
                                            />
                                        </Form.Group>
                                    </Row>

                                    <Form.Group className="mb-4">
                                        <Form.Label>Email </Form.Label>
                                        <InputGroup className="mb-3">
                                            <FormControl
                                                type="text"
                                                aria-label="email"
                                                name="email"
                                                value={localUserData.email}
                                                disabled
                                            />
                                            {localUserData.emailVerified ===
                                                true && (
                                                    <InputGroup.Append>
                                                        <InputGroup.Text id="basic-addon2">
                                                            <span className="material-icons green">
                                                                check_circle
                                                            </span>
                                                        </InputGroup.Text>
                                                    </InputGroup.Append>
                                                )}
                                        </InputGroup>
                                    </Form.Group>
                                    {!APP_CONFIG.APP_IS_ONPREM && (
                                        <Form.Group className="mb-4">
                                            <Form.Label>
                                                Mobile Number
                                            </Form.Label>
                                            <InputGroup className="mb-3">
                                                <Form.Control
                                                    hidden
                                                    isInvalid={
                                                        !!localUserData.phoneNumber &&
                                                        !isValidPhoneNumber(
                                                            localUserData.phoneNumber
                                                        )
                                                    }
                                                    ref={phoneNumRef}
                                                />
                                                <StyledPhoneInput
                                                    disabled={!isEdit}
                                                    international
                                                    className="form-control"
                                                    defaultCountry="SG"
                                                    value={
                                                        localUserData.phoneNumber
                                                    }
                                                    aria-label="phoneNumber"
                                                    name="phoneNumber"
                                                    onChange={(e: any) =>
                                                        setLocalUserData({
                                                            ...localUserData,
                                                            phoneNumber: e,
                                                        })
                                                    }
                                                    onKeyPress={submitForm}
                                                />
                                                {localUserData.phoneNumberVerified && (
                                                    <InputGroup.Append>
                                                        <InputGroup.Text id="basic-addon2">
                                                            <span className="material-icons green">
                                                                check_circle
                                                            </span>
                                                        </InputGroup.Text>
                                                    </InputGroup.Append>
                                                )}
                                                {userData.phoneNumber &&
                                                    !userData.phoneNumberVerified && (
                                                        <Button
                                                            variant="primary"
                                                            className="ml-2 py-0"
                                                            onClick={
                                                                handleClickVerify
                                                            }
                                                        >
                                                            Verify
                                                        </Button>
                                                    )}
                                                <Form.Control.Feedback type="invalid">
                                                    {
                                                        PHONE_NUMBER_VALIDATION_MESSAGE
                                                    }
                                                </Form.Control.Feedback>
                                            </InputGroup>
                                        </Form.Group>
                                    )}
                                </div>

                                {/* Will enable this if phone_number is set */}
                                {userData.phoneNumber &&
                                    !APP_CONFIG.APP_IS_ONPREM &&
                                    userData.phoneNumberVerified && (
                                        <div className="form-box mt-3">
                                            <CustomSwitch
                                                name="mfa"
                                                id={"mfa"}
                                                checked={isMFAEnable}
                                                onChange={handleMFAChange}
                                                ref={mfaRef}
                                                label={
                                                    <span className="mr-4">
                                                        Multifactor
                                                        Authentication
                                                    </span>
                                                }
                                            />
                                        </div>
                                    )}
                            </Form>
                            {isEdit && (
                                <>
                                    <Button
                                        variant="secondary"
                                        className="mt-4 mr-2"
                                        onClick={handleCancel}
                                    >
                                        CANCEL
                                    </Button>
                                    <Button
                                        variant="primary"
                                        className="mt-4"
                                        onClick={handleSave}
                                    >
                                        SAVE
                                    </Button>
                                </>
                            )}
                        </Col>
                    </Row>
                </Container>

                <Modal
                    show={isShow}
                    onHide={handleCloseModal}
                    keyboard={false}
                    centered
                    size="sm"
                    className="verify-phone-number"
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Verification</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p className="main-message mb-3">
                            Please enter OTP code sent to your mobile number.
                        </p>
                        <Form.Group className="mb-3" controlId="otp">
                            <Form.Control
                                type="text"
                                placeholder="Enter OTP"
                                aria-label="otp"
                                onChange={handleChangeVerifyCode}
                                autoComplete="off"
                            />
                            {codeError && (
                                <p
                                    role="alert"
                                    aria-label="errorMessage"
                                    className="error-message my-0 mt-1"
                                >
                                    {codeError}
                                </p>
                            )}
                        </Form.Group>
                        <div className="mt-4 resend-code">
                            Didn’t get code?
                            {countdown > 0 ? (
                                <span className="pl-1 green">
                                    {renderCountdown(countdown)}
                                </span>
                            ) : (
                                <Button
                                    variant="link"
                                    className="px-1"
                                    onClick={openConfirmationResendModal}
                                >
                                    Resend code
                                </Button>
                            )}
                        </div>
                        <Button
                            variant="primary"
                            onClick={handleSubmitCode}
                            className="mt-4 w-100 py-2"
                        >
                            Verify
                        </Button>
                    </Modal.Body>
                </Modal>
                <LoadingModal showModal={!isModalLoaded} />
                <DefaultModal
                    modalType={modalType}
                    modalShow={modalShow}
                    setModalShow={setModalShow}
                    resendFunction={handleClickVerify}
                    modalContent={modalContent}
                />
            </div>
        </ContentWrapper>
    );
};

export default UserProfile;
